import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Loading } from '@/components/ui/Loading';
import { Layout } from '@/components/layout';

// Lazy load pages for code splitting
const Login = lazy(() => import('@/pages/Login'));
const Home = lazy(() => import('@/pages/Home'));
const Smoelenboek = lazy(() => import('@/pages/Smoelenboek'));
const Repertoire = lazy(() => import('@/pages/Repertoire'));
const RepertoireBeheer = lazy(() => import('@/pages/RepertoireBeheer'));
const RepertoireImport = lazy(() => import('@/pages/RepertoireImport'));
const SongDetail = lazy(() => import('@/pages/SongDetail'));
const MarkeringenEditor = lazy(() => import('@/pages/MarkeringenEditor'));
const MarkeringenInstellingen = lazy(() => import('@/pages/MarkeringenInstellingen'));
const Playlists = lazy(() => import('@/pages/Playlists'));
const PlaylistDetail = lazy(() => import('@/pages/PlaylistDetail'));
const Dirigent = lazy(() => import('@/pages/Dirigent'));
const Downloads = lazy(() => import('@/pages/Downloads'));
const Berichten = lazy(() => import('@/pages/Berichten'));
const BerichtNieuw = lazy(() => import('@/pages/BerichtNieuw'));
const BerichtDetail = lazy(() => import('@/pages/BerichtDetail'));
const BerichtBewerk = lazy(() => import('@/pages/BerichtBewerk'));
const Admin = lazy(() => import('@/pages/Admin'));
const AdminInstellingen = lazy(() => import('@/pages/AdminInstellingen'));
const MediaGalerij = lazy(() => import('@/pages/MediaGalerij'));
const MediaAlbum = lazy(() => import('@/pages/MediaAlbum'));
const Instellingen = lazy(() => import('@/pages/Instellingen'));
const NotFound = lazy(() => import('@/pages/NotFound'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <Home />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/smoelenboek"
          element={
            <ProtectedRoute>
              <Layout>
                <Smoelenboek />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/repertoire"
          element={
            <ProtectedRoute>
              <Layout>
                <Repertoire />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/repertoire/beheer"
          element={
            <ProtectedRoute>
              <Layout>
                <RepertoireBeheer />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/repertoire/import"
          element={
            <ProtectedRoute>
              <Layout>
                <RepertoireImport />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/repertoire/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <SongDetail />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/repertoire/:id/markeringen"
          element={
            <ProtectedRoute>
              <Layout>
                <MarkeringenEditor />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/playlists"
          element={
            <ProtectedRoute>
              <Layout>
                <Playlists />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/playlists/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <PlaylistDetail />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dirigent"
          element={
            <ProtectedRoute>
              <Layout>
                <Dirigent />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/downloads"
          element={
            <ProtectedRoute>
              <Layout>
                <Downloads />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/berichten"
          element={
            <ProtectedRoute>
              <Layout>
                <Berichten />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/berichten/nieuw"
          element={
            <ProtectedRoute>
              <Layout>
                <BerichtNieuw />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/berichten/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <BerichtDetail />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/berichten/:id/bewerk"
          element={
            <ProtectedRoute>
              <Layout>
                <BerichtBewerk />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/media"
          element={
            <ProtectedRoute>
              <Layout>
                <MediaGalerij />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/media/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <MediaAlbum />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Layout>
                <Admin />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/instellingen"
          element={
            <ProtectedRoute>
              <Layout>
                <AdminInstellingen />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/instellingen"
          element={
            <ProtectedRoute>
              <Layout>
                <Instellingen />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/instellingen/markeringen"
          element={
            <ProtectedRoute>
              <Layout>
                <MarkeringenInstellingen />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
